// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useCallback } from 'react';

import type { TaskType, TaskTypeGroup } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { ManageTaskGroupsModal } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/ManageTaskGroupsModal';

import { CreateEditTaskTypeModal } from './CreateEditTaskType/CreateEditTaskTypeModal';
import { TaskTypeGroupsSelect, type TaskTypeGroupsSelectProps } from './TaskTypeGroupsSelect';
import { useTaskTypes } from './useTaskTypes';

type Props = {
  /** @defaultValue false */
  withHeader?: boolean;
  rightSideComponent?: (
    selectedTasks: TaskType[],
    selectedGroups: TaskTypeGroup[],
    reset: (refresh?: boolean) => void,
  ) => ReactNode;
} & TaskTypeGroupsSelectProps;

export const TaskAdder = ({ withHeader, rightSideComponent, ...props }: Props) => {
  const {
    selectedGroups,
    selectedTasks,
    searchText,
    setSearchText,
    groupsPaginatedGet,
    setSelectedTasks,
    setSelectedGroups,
  } = useTaskTypes();

  const { reset: resetPaginatedGet } = groupsPaginatedGet;

  const reset = useCallback(
    (refresh = false) => {
      setSelectedTasks([]);
      setSelectedGroups([]);
      if (refresh) resetPaginatedGet();
    },
    [resetPaginatedGet, setSelectedGroups, setSelectedTasks],
  );

  return (
    <>
      {withHeader && (
        <Text fz="lg" fw={600} mb="xs">
          Tasks
        </Text>
      )}
      <Stack h="100%" flex={1}>
        <Flex justify="space-between" gap="sm" wrap="wrap">
          <TextInputDebounced value={searchText} onChange={setSearchText} placeholder="Search..." miw={200} />
          <Flex gap="sm">
            <ManageTaskGroupsModal onRefreshTaskGroups={() => groupsPaginatedGet.reset()} />
            <CreateEditTaskTypeModal />
            {rightSideComponent?.(selectedTasks, selectedGroups, reset)}
          </Flex>
        </Flex>
        <TaskTypeGroupsSelect {...props} />
      </Stack>
    </>
  );
};
